<template>
    <div>
        <loader v-if="isLoading" />
        <div v-else>
            <p v-if="errorMessage !== ''">{{ errorMessage }}</p>
            <div v-else class="list">
                <b-row>
                    <b-col cols="4">
                        <b>Diagnosis:</b>
                    </b-col>
                    <b-col>
                        <b>Used:</b>
                    </b-col>
                    <b-col>
                        <b>Created:</b>
                    </b-col>
                    <b-col>
                        <b>Created By:</b>
                    </b-col>
                    <b-col>
                        <b>Updated:</b>
                    </b-col>
                    <b-col>
                        <b>Updated By:</b>
                    </b-col>
                    <b-col cols="1" />
                </b-row>

                <div v-if="list.length > 0">
                    <b-row
                        v-for="record in list"
                        :key="record.id"
                        class="row-record d-flex align-items-center"
                    >
                        <b-col cols="4">
                            {{ record.description }}
                        </b-col>
                        <b-col>{{ record.use_count }} times</b-col>
                        <b-col>
                            {{ properDate(record.create_at.date) }}
                        </b-col>
                        <b-col>
                            {{ record.create_by.display_name }}
                        </b-col>
                        <b-col>
                            {{ properDate(record.update_at.date) }}
                        </b-col>
                        <b-col>
                            {{ record.update_by.display_name }}
                        </b-col>
                        <b-col cols="1" class="text-right">
                            <b-dropdown
                                :disabled="
                                    isLoadingOptions &&
                                    currentReactivateId === record.id
                                "
                                size="sm"
                                variant="link"
                                right
                                no-caret
                            >
                                <template v-slot:button-content>
                                    <i class="cus-cog-down"></i>
                                </template>
                                <b-dropdown-item @click="editDiagnosis(record)">
                                    <i class="cus-page-white-edit"></i>
                                    Edit Diagnosis
                                </b-dropdown-item>

                                <b-dropdown-item
                                    v-if="record.status === 3"
                                    @click="reactivate(record.id)"
                                >
                                    <i class="cus-add"></i> Reactivate Diagnosis
                                </b-dropdown-item>

                                <b-dropdown-item
                                    v-if="
                                        record.status !== 3 &&
                                        record.use_count > 0
                                    "
                                    @click="openModalSuspend(record.id)"
                                >
                                    <i class="cus-delete"></i> Suspend Diagnosis
                                </b-dropdown-item>

                                <b-dropdown-item
                                    v-if="
                                        record.status !== 3 &&
                                        record.use_count <= 0
                                    "
                                    @click="openModalDelete(record.id)"
                                >
                                    <i class="cus-delete"></i> Delete Diagnosis
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-col>
                    </b-row>
                    <b-pagination
                        v-if="meta"
                        v-show="meta.lastPage > 1 && !isLoading"
                        class="vet-pagination"
                        align="right"
                        v-model="meta.currentPage"
                        :total-rows="meta.total"
                        :per-page="meta.perPage"
                        prev-text="Prev"
                        next-text="Next"
                        :hide-goto-end-buttons="true"
                        @change="getList"
                    />
                </div>
                <div v-else>
                    <b-row class="row-record">
                        <b-col>
                            <i>No records available.</i>
                        </b-col>
                    </b-row>
                </div>
            </div>
            <div></div>
        </div>

        <modal-delete
            ref="modalDelete"
            :reloadAfter="true"
            @reloadlist="reloadList"
        />

        <modal-suspend
            ref="modalSuspend"
            :reloadAfter="true"
            @reloadlist="reloadList"
        />
    </div>
</template>

<script>
import modalDelete from "./modal-delete";
import modalSuspend from "./modal-suspend";

export default {
    components: {
        modalDelete,
        modalSuspend,
        Loader: () => import("@/components/elements/loader-list")
    },
    props: {
        filter: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            isLoading: true,
            errorMessage: "",
            list: [],
            isLoadingOptions: false,
            currentReactivateId: null,
            meta: null
        };
    },
    watch: {
        filter(value) {
            this.fetchList(value);
        }
    },
    mounted() {
        this.fetchList(this.filter);
    },
    methods: {
        getList(page) {
            this.fetchList(this.filter, page);
        },
        async fetchList(filterType, page = 1) {
            this.isLoading = true;
            this.errorMessage = "";

            const mapValue = {
                ACTIVE: 1,
                SUSPENDED: 3
            };

            try {
                const res = await this.$http.get(
                    process.env.BARRAMUNDI + "account/custom-diagnosis",
                    {
                        params: {
                            page: page ? page : 1,
                            perPage: 50,
                            status: mapValue[filterType]
                        }
                    }
                );

                this.isLoading = false;

                if (res.data.response.message.code === 200) {
                    this.list = res.data.response.resources;
                    this.meta = res.data.response.meta;
                } else {
                    throw new Error("Unable to get Custom Diagnoses list.");
                }
            } catch (e) {
                this.isLoading = false;
                this.errorMessage = "Error: " + e.message;
            }
        },
        editDiagnosis(record) {
            this.$emit("editDiagnosis", record);
        },
        properDate(rawDate) {
            return this._toDateTime(rawDate)
                .setZone(this._timeZone)
                .toFormat(this._getLuxonFormat());
        },
        openModalDelete(recordId) {
            this.$refs.modalDelete.open(recordId);
        },
        closeModalDelete() {
            this.$refs.modalDelete.close();
        },
        openModalSuspend(recordId) {
            this.$refs.modalSuspend.open(recordId);
        },
        closeModalSuspend() {
            this.$refs.modalSuspend.close();
        },
        reloadList() {
            this.fetchList(this.filter);
        },
        async reactivate(recordId) {
            try {
                this.isLoadingOptions = true;
                this.currentReactivateId = recordId;

                await this.$http.post(
                    `${process.env.BARRAMUNDI}account/reactivate-custom-diagnosis/${recordId}`,
                    {
                        status: 1
                    }
                );

                this.isLoadingOptions = false;
                this.currentReactivateId = null;
                this.fetchList(this.filter);
                this._message("Diagnosis reactivated successfully");
            } catch (e) {
                this.isLoadingOptions = false;
                this.currentReactivateId = null;
                this._message("Diagnosis reactivated successfully", "danger");
            }
        }
    }
};
</script>

<style scoped>
.initials {
    display: block;
    margin-bottom: 20px;
}
.list {
    margin: 0 15px 10px 15px;
}
.row-record {
    padding: 7px 0;
    border-bottom: 1px solid #ddd;
}
.row-record:hover {
    background-color: #f5f5f5;
}
</style>
